import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, InputAdornment, Chip, Box,
  Tabs, Tab, TablePagination, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemIcon, ListItemText, Checkbox, Select, MenuItem, InputLabel, FormControl
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StarIcon from '@mui/icons-material/Star';

import '../../../../assets/styles/Admin/AdminDashboardOverview.css';
import { BACKEND_DOMAIN } from '../../../../common/env';

const AdminDashboardAuctionDetails = () => {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // 0: Details, 1: Teams, 2: Players
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [searchTeam, setSearchTeam] = useState('');
  const [searchPlayer, setSearchPlayer] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [liveStreamUrl, setLiveStreamUrl] = useState('');
  const [showUpdateAuctionModal, setShowUpdateAuctionModal] = useState(false);
  const [auctionData, setAuctionData] = useState({
    title: '',
    sport: 'cricket',
    organizer: '',
    date: '',
    location: '',
    defaultTeamBudgets: '',
    playersPerTeam: '',
    baseValue: ''
  });
  
  // Add state for auction logo file
  const [auctionLogoFile, setAuctionLogoFile] = useState(null);
  
  // Modals for teams and players
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showUpdateTeamModal, setShowUpdateTeamModal] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showUpdatePlayerModal, setShowUpdatePlayerModal] = useState(false);
  
  // Team and player data
  const [selectedTeam, setSelectedTeam] = useState({
    id: '',
    teamName: '',
    ownerName: '',
    teamInitials: '',
    teamLogo: '',
    teamBudget: '',
    email: '',
    phoneNumber: ''
  });
  
  const [newTeam, setNewTeam] = useState({
    teamName: '',
    ownerName: '',
    teamInitials: '',
    teamLogo: '',
    teamBudget: '',
    email: '',
    phoneNumber: '',
    teamOwnerLogo: ''
  });
  
  // Add state for selected player
  const [selectedPlayer, setSelectedPlayer] = useState({
    _id: '',
    playerName: '',
    playerLogo: '',
    playerType: '',
    baseValue: '',
    matches: '',
    runs: '',
    wickets: '',
    strikeRate: '',
    halfCenturies: '',
    centuries: ''
  });
  
  // Add state to track players per team
  const [playersPerTeam, setPlayersPerTeam] = useState(0);
  
  const [playerData, setPlayerData] = useState({
    playerName: '',
    playerLogo: '',
    playerType: '',
    baseValue: '',
    matches: '',
    runs: '',
    wickets: '',
    strikeRate: '',
    halfCenturies: '',
    centuries: ''
  });

  // State for icon player management
  const [showIconPlayerModal, setShowIconPlayerModal] = useState(false);
  const [selectedTeamForIcon, setSelectedTeamForIcon] = useState(null);
  const [showCreateIconPlayerModal, setShowCreateIconPlayerModal] = useState(false);
  const [selectedIconPlayers, setSelectedIconPlayers] = useState([]);
  
  const [newIconPlayer, setNewIconPlayer] = useState({
    playerName: '',
    playerLogo: '',
    playerType: '',
    baseValue: '',
    matches: '',
    runs: '',
    wickets: '',
    strikeRate: '',
    halfCenturies: '',
    centuries: ''
  });

  // Function to open icon player selection modal
  const openIconPlayerModal = (team) => {
    setSelectedTeamForIcon(team);
    setSelectedIconPlayers([]); // Reset selection when opening modal
    setShowIconPlayerModal(true);
  };

  // Function to close icon player selection modal
  const closeIconPlayerModal = () => {
    setShowIconPlayerModal(false);
    setSelectedTeamForIcon(null);
    setSelectedIconPlayers([]);
  };

  // Function to open create icon player modal
  const openCreateIconPlayerModal = () => {
    setShowCreateIconPlayerModal(true);
  };

  // Function to close create icon player modal
  const closeCreateIconPlayerModal = () => {
    setShowCreateIconPlayerModal(false);
    setNewIconPlayer({
      playerName: '',
      playerLogo: '',
      playerType: '',
      baseValue: '',
      matches: '',
      runs: '',
      wickets: '',
      strikeRate: '',
      halfCenturies: '',
      centuries: ''
    });
  };

  // Handle new icon player input change
  const handleNewIconPlayerChange = (e) => {
    const { name, value } = e.target;
    setNewIconPlayer(prev => ({ ...prev, [name]: value }));
  };

  // Handle create icon player
  const handleCreateIconPlayer = async () => {
    try {
      const formData = new FormData();
      formData.append('playerName', newIconPlayer.playerName);
      formData.append('playerType', newIconPlayer.playerType);
      formData.append('isIcon', true); // Mark as icon player
      
      // Only append numeric values if they are not empty
      if (newIconPlayer.baseValue !== '' && newIconPlayer.baseValue !== undefined) {
        formData.append('baseValue', newIconPlayer.baseValue);
      }
      if (newIconPlayer.matches !== '' && newIconPlayer.matches !== undefined) {
        formData.append('matches', newIconPlayer.matches);
      }
      if (newIconPlayer.wickets !== '' && newIconPlayer.wickets !== undefined) {
        formData.append('wickets', newIconPlayer.wickets);
      }
      if (newIconPlayer.runs !== '' && newIconPlayer.runs !== undefined) {
        formData.append('runs', newIconPlayer.runs);
      }
      if (newIconPlayer.strikeRate !== '' && newIconPlayer.strikeRate !== undefined) {
        formData.append('strikeRate', newIconPlayer.strikeRate);
      }
      if (newIconPlayer.halfCenturies !== '' && newIconPlayer.halfCenturies !== undefined) {
        formData.append('halfCenturies', newIconPlayer.halfCenturies);
      }
      if (newIconPlayer.centuries !== '' && newIconPlayer.centuries !== undefined) {
        formData.append('centuries', newIconPlayer.centuries);
      }

      const response = await axios.post(`${BACKEND_DOMAIN}/auctions/${auctionId}/createPlayer`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setPlayers([...players, response.data.player]);
      closeCreateIconPlayerModal();
      alert('Icon player created successfully!');
    } catch (err) {
      console.error('Error creating icon player:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create icon player. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  // Handle icon player selection
  const handleIconPlayerSelect = (playerId) => {
    setSelectedIconPlayers(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  // Save selected icon players
  const handleSaveIconPlayers = async () => {
    if (!selectedTeamForIcon) return;
    
    try {
      // For each selected player, add them as an icon player to the team
      const promises = selectedIconPlayers.map(playerId => 
        axios.post(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams/${selectedTeamForIcon._id}/icon-players`, 
          { playerId }, 
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      
      await Promise.all(promises);
      
      // Refresh teams data to reflect changes
      const teamsResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeams(teamsResponse.data.teams || []);
      
      // Refresh players data to reflect sold status
      const playersResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/players`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlayers(playersResponse.data.players || []);
      
      closeIconPlayerModal();
      alert('Icon players added successfully!');
    } catch (error) {
      console.error('Error saving icon players:', error);
      alert('Failed to save icon players. Please try again.');
    }
  };

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (auctionId) {
      fetchAuctionDetails();
    }
  }, [auctionId]);

  const fetchAuctionDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuction(auctionResponse.data);
      setLiveStreamUrl(auctionResponse.data.liveAuctionLink || '');
      
      // Set players per team from auction data
      setPlayersPerTeam(auctionResponse.data.playersPerTeam || 0);
      
      // Fetch teams
      const teamsResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeams(teamsResponse.data.teams || []);
      
      // Fetch players
      const playersResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/players`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlayers(playersResponse.data.players || []);
    } catch (error) {
      console.error('Error fetching auction details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'upcoming': return '#059669';
      case 'ongoing': return '#d97706';
      case 'past': return '#7c3aed';
      default: return '#64748b';
    }
  };

  const getAuctionStatus = (auction) => {
    const currentDate = new Date();
    const auctionDate = new Date(auction.date);
    
    if (auctionDate > currentDate) return 'Upcoming';
    if (auctionDate.toDateString() === currentDate.toDateString()) return 'Ongoing';
    return 'Past';
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Team management functions
  const handleAddTeam = async () => {
    try {
      const formData = new FormData();
      formData.append('teamName', newTeam.teamName);
      formData.append('ownerName', newTeam.ownerName);
      formData.append('teamInitials', newTeam.teamInitials);
      formData.append('teamLogo', newTeam.teamLogo);
      formData.append('teamBudget', newTeam.teamBudget);
      formData.append('email', newTeam.email);
      formData.append('phoneNumber', newTeam.phoneNumber);
      formData.append('teamOwnerLogo', newTeam.teamOwnerLogo);

      const response = await axios.post(`${BACKEND_DOMAIN}/auctions/${auctionId}/createTeam`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setTeams([...teams, response.data.team]);
      setShowAddTeamModal(false);
      setNewTeam({
        teamName: '',
        ownerName: '',
        teamInitials: '',
        teamLogo: '',
        teamBudget: '',
        email: '',
        phoneNumber: '',
        teamOwnerLogo: ''
      });
      alert('Team added successfully!');
    } catch (err) {
      console.error('Error adding team:', err);
      alert('Failed to add team. Please try again.');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this team?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeams(teams.filter((team) => team._id !== teamId));
      alert('Team deleted successfully!');
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('Failed to delete team. Please try again.');
    }
  };

  // Player management functions  
  const handleAddPlayer = async () => {
    try {
      const formData = new FormData();
      formData.append('playerName', playerData.playerName);
      formData.append('playerType', playerData.playerType);
      
      // Only append numeric values if they are not empty
      if (playerData.baseValue !== '' && playerData.baseValue !== undefined) {
        formData.append('baseValue', playerData.baseValue);
      }
      if (playerData.matches !== '' && playerData.matches !== undefined) {
        formData.append('matches', playerData.matches);
      }
      if (playerData.wickets !== '' && playerData.wickets !== undefined) {
        formData.append('wickets', playerData.wickets);
      }
      if (playerData.runs !== '' && playerData.runs !== undefined) {
        formData.append('runs', playerData.runs);
      }
      if (playerData.strikeRate !== '' && playerData.strikeRate !== undefined) {
        formData.append('strikeRate', playerData.strikeRate);
      }
      if (playerData.halfCenturies !== '' && playerData.halfCenturies !== undefined) {
        formData.append('halfCenturies', playerData.halfCenturies);
      }
      if (playerData.centuries !== '' && playerData.centuries !== undefined) {
        formData.append('centuries', playerData.centuries);
      }
      
      // Handle file upload properly
      if (playerData.playerLogo && playerData.playerLogo instanceof File) {
        formData.append('playerLogo', playerData.playerLogo);
      }

      const response = await axios.post(`${BACKEND_DOMAIN}/auctions/${auctionId}/createPlayer`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setPlayers([...players, response.data.player]);
      setShowAddPlayerModal(false);
      setPlayerData({
        playerName: '',
        playerLogo: '',
        playerType: '',
        baseValue: '',
        matches: '',
        runs: '',
        wickets: '',
        strikeRate: '',
        halfCenturies: '',
        centuries: ''
      });
      alert('Player added successfully!');
    } catch (err) {
      console.error('Error adding player:', err);
      // Provide more detailed error message
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add player. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this player?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/${playerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlayers(players.filter((player) => player._id !== playerId));
      alert('Player deleted successfully!');
    } catch (error) {
      console.error('Error deleting player:', error);
      alert('Failed to delete player. Please try again.');
    }
  };

  // Handle team update
  const handleUpdateTeam = async () => {
    try {
      const formData = new FormData();
      formData.append('teamName', selectedTeam.teamName);
      formData.append('ownerName', selectedTeam.ownerName);
      formData.append('teamInitials', selectedTeam.teamInitials);
      formData.append('teamBudget', selectedTeam.teamBudget);
      formData.append('email', selectedTeam.email);
      formData.append('phoneNumber', selectedTeam.phoneNumber);
      
      // Only append files if they are actually files (not strings)
      if (selectedTeam.teamLogo && selectedTeam.teamLogo instanceof File) {
        formData.append('teamLogo', selectedTeam.teamLogo);
      }
      
      if (selectedTeam.teamOwnerLogo && selectedTeam.teamOwnerLogo instanceof File) {
        formData.append('teamOwnerLogo', selectedTeam.teamOwnerLogo);
      }

      const response = await axios.put(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams/${selectedTeam._id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update the team in the state
      setTeams(teams.map(team => team._id === selectedTeam._id ? response.data.team : team));
      setShowUpdateTeamModal(false);
      setSelectedTeam({
        _id: '',
        teamName: '',
        ownerName: '',
        teamInitials: '',
        teamLogo: '',
        teamBudget: '',
        email: '',
        phoneNumber: '',
        teamOwnerLogo: ''
      });
      alert('Team updated successfully!');
    } catch (err) {
      console.error('Error updating team:', err);
      alert('Failed to update team. Please try again.');
    }
  };

  // Handle player update
  const handleUpdatePlayer = async () => {
    try {
      const formData = new FormData();
      formData.append('playerName', selectedPlayer.playerName);
      formData.append('playerType', selectedPlayer.playerType);
      
      // Only append numeric values if they are not empty
      if (selectedPlayer.baseValue !== '' && selectedPlayer.baseValue !== undefined) {
        formData.append('baseValue', selectedPlayer.baseValue);
      }
      if (selectedPlayer.matches !== '' && selectedPlayer.matches !== undefined) {
        formData.append('matches', selectedPlayer.matches);
      }
      if (selectedPlayer.runs !== '' && selectedPlayer.runs !== undefined) {
        formData.append('runs', selectedPlayer.runs);
      }
      if (selectedPlayer.wickets !== '' && selectedPlayer.wickets !== undefined) {
        formData.append('wickets', selectedPlayer.wickets);
      }
      if (selectedPlayer.strikeRate !== '' && selectedPlayer.strikeRate !== undefined) {
        formData.append('strikeRate', selectedPlayer.strikeRate);
      }
      if (selectedPlayer.halfCenturies !== '' && selectedPlayer.halfCenturies !== undefined) {
        formData.append('halfCenturies', selectedPlayer.halfCenturies);
      }
      if (selectedPlayer.centuries !== '' && selectedPlayer.centuries !== undefined) {
        formData.append('centuries', selectedPlayer.centuries);
      }
      
      // Only append file if it's actually a file (not a string)
      if (selectedPlayer.playerLogo && selectedPlayer.playerLogo instanceof File) {
        formData.append('playerLogo', selectedPlayer.playerLogo);
      }

      const response = await axios.put(`${BACKEND_DOMAIN}/auctions/${auctionId}/player/${selectedPlayer._id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update the player in the state
      setPlayers(players.map(player => player._id === selectedPlayer._id ? response.data.player : player));
      setShowUpdatePlayerModal(false);
      setSelectedPlayer({
        _id: '',
        playerName: '',
        playerLogo: '',
        playerType: '',
        baseValue: '',
        matches: '',
        runs: '',
        wickets: '',
        strikeRate: '',
        halfCenturies: '',
        centuries: ''
      });
      alert('Player updated successfully!');
    } catch (err) {
      console.error('Error updating player:', err);
      // Provide more detailed error message
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update player. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  // Handle file input changes for new team
  const handleNewTeamFileChange = (e) => {
    const { name, files } = e.target;
    setNewTeam(prev => ({ ...prev, [name]: files[0] }));
  };

  // Handle file input changes for selected team
  const handleSelectedTeamFileChange = (e) => {
    const { name, files } = e.target;
    setSelectedTeam(prev => ({ ...prev, [name]: files[0] }));
  };

  // Handle file input changes for player
  const handlePlayerFileChange = (e) => {
    const { name, files } = e.target;
    if (showUpdatePlayerModal) {
      setSelectedPlayer(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setPlayerData(prev => ({ ...prev, [name]: files[0] }));
    }
  };

  // Filtering functions
  const filteredTeams = teams.filter((team) =>
    team.teamName.toLowerCase().includes(searchTeam.toLowerCase()) ||
    team.teamInitials.toLowerCase().includes(searchTeam.toLowerCase())
  );

  const filteredPlayers = players.filter((player) =>
    player.playerName.toLowerCase().includes(searchPlayer.toLowerCase())
  );

  const playersToDisplay = filteredPlayers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle live stream URL changes
  const handleLiveStreamUrlChange = (url) => {
    setLiveStreamUrl(url);
  };

  // Update live stream URL
  const handleUpdateLiveStreamUrl = async () => {
    try {
      await axios.put(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
        liveAuctionLink: liveStreamUrl
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      alert('Live stream URL updated successfully!');
      fetchAuctionDetails(); // Refresh to show updated data
    } catch (error) {
      console.error('Error updating live stream URL:', error);
      alert('Failed to update live stream URL. Please try again.');
    }
  };

  // Generate OBS overlay link for existing auctions
  const handleGenerateObsLink = async () => {
    try {
      await axios.post(`${BACKEND_DOMAIN}/auctions/${auctionId}/generate-obs-link`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      alert('OBS overlay link generated successfully!');
      fetchAuctionDetails(); // Refresh to show updated data
    } catch (error) {
      console.error('Error generating OBS overlay link:', error);
      alert('Failed to generate OBS overlay link. Please try again.');
    }
  };

  // Handle auction input change
  const handleAuctionInputChange = (e) => {
    const { name, value } = e.target;
    setAuctionData(prev => ({ ...prev, [name]: value }));
  };

  // Handle auction logo file change
  const handleAuctionLogoChange = (e) => {
    setAuctionLogoFile(e.target.files[0]);
  };

  // Open update auction modal
  const openUpdateAuctionModal = () => {
    setAuctionData({
      title: auction.title || '',
      sport: auction.sport || 'cricket',
      organizer: auction.organizer || '',
      date: auction.date ? new Date(auction.date).toISOString().slice(0, 16) : '',
      location: auction.location || '',
      defaultTeamBudgets: auction.defaultTeamBudgets || '',
      playersPerTeam: auction.playersPerTeam || '',
      baseValue: auction.baseValue || ''
    });
    setAuctionLogoFile(null); // Reset file input
    setShowUpdateAuctionModal(true);
  };

  // Open add team modal with default values
  const openAddTeamModal = () => {
    setNewTeam({
      teamName: '',
      ownerName: '',
      teamInitials: '',
      teamLogo: '',
      teamBudget: auction?.defaultTeamBudgets || '',
      email: '',
      phoneNumber: '',
      teamOwnerLogo: ''
    });
    setShowAddTeamModal(true);
  };

  // Handle auction update
  const handleUpdateAuction = async () => {
    try {
      // Use FormData to handle file uploads
      const formData = new FormData();
      
      // Append all auction data fields
      Object.keys(auctionData).forEach(key => {
        if (key === 'defaultTeamBudgets' || key === 'playersPerTeam' || key === 'baseValue') {
          formData.append(key, Number(auctionData[key]));
        } else {
          formData.append(key, auctionData[key]);
        }
      });
      
      // Append auction logo file if selected
      if (auctionLogoFile) {
        formData.append('auctionLogo', auctionLogoFile);
      }

      await axios.put(`${BACKEND_DOMAIN}/auctions/${auctionId}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Auction updated successfully!');
      setShowUpdateAuctionModal(false);
      fetchAuctionDetails(); // Refresh to show updated data
    } catch (error) {
      console.error('Error updating auction:', error);
      alert('Failed to update auction. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-overview">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading auction details...</p>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="admin-dashboard-overview">
        <div className="loading-container">
          <Typography variant="h5">Auction not found</Typography>
          <Button 
            onClick={() => navigate('/admin-dashboard')} 
            variant="contained" 
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-overview">
      {/* Auction Details Header */}
      <Card className="auction-details-card" sx={{ mt: 1 }}>
        <CardContent>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
            <Box sx={{ flex: 1, textAlign: { xs: 'left', sm: 'center' } }}>
              <Typography variant="h4" sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}>
                {auction.title}
              </Typography>
            </Box>
            <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
              <Chip
                label={getAuctionStatus(auction)}
                style={{
                  backgroundColor: getStatusColor(getAuctionStatus(auction)),
                  color: 'white',
                  fontWeight: 600
                }}
                size="small"
              />
            </Box>
          </Box>

          {/* Selected Auction Info & Actions */}
          <Box sx={{ mb: 3, p: { xs: 2, sm: 3 }, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {auction.organizer} • {new Date(auction.date).toLocaleDateString()} • {auction.location}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'row', sm: 'row' }, 
                    gap: 1, 
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'stretch', sm: 'flex-end' },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={openUpdateAuctionModal}
                      variant="outlined"
                      color="primary"
                      sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 'inherit' } }}
                    >
                      Edit Auction
                    </Button>
                    <Button
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => navigate(`/live-auction/${auction._id}/admin-live-bidding`)}
                      variant="contained"
                      color="primary"
                      sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 'inherit' } }}
                    >
                      Go Live
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Tabs for Details, Teams, Players */}
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                minWidth: { xs: 'auto', sm: 90 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
            variant={window.innerWidth < 600 ? 'fullWidth' : 'standard'}
          >
            <Tab label="Details" />
            <Tab label="Teams" />
            <Tab label="Players" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ mt: 3 }}>
            {/* Details Tab */}
            {activeTab === 0 && (
              <React.Fragment>
                <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Auction Details</Typography>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Title</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {auction.title}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Sport</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {auction.sport}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Organizer</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {auction.organizer}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {new Date(auction.date).toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Location</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {auction.location}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Team Budget</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                        ${auction.defaultTeamBudgets}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Players/Team</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {auction.playersPerTeam}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">Base Value</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        ${auction.baseValue}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>  
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Live Stream URL</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Enter YouTube live stream URL"
                          value={liveStreamUrl || ''}
                          onChange={(e) => handleLiveStreamUrlChange(e.target.value)}
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleUpdateLiveStreamUrl}
                          sx={{ minWidth: 'auto', px: 2 }}
                        >
                          Save
                        </Button>
                      </Box>
                      {auction.liveAuctionLink && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                          ✓ Live stream configured - Users can now watch the stream
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'primary.50' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>OBS Overlay Link</Typography>
                      {auction.obsOverlayLink ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={auction.obsOverlayLink.replace('localhost:3000', 'myliveauction.com')}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      if (auction.obsOverlayLink) {
                                        const updatedLink = auction.obsOverlayLink.replace('localhost:3000', 'myliveauction.com');
                                        navigator.clipboard.writeText(updatedLink);
                                        alert('OBS overlay link copied to clipboard!');
                                      }
                                    }}
                                    sx={{ mr: -1 }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            sx={{ 
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.875rem',
                                bgcolor: 'background.paper'
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              if (auction.obsOverlayLink) {
                                const updatedLink = auction.obsOverlayLink.replace('localhost:3000', 'myliveauction.com');
                                window.open(updatedLink, '_blank');
                              }
                            }}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Preview
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            size="small"
                            value="Click 'Generate' to create OBS overlay link"
                            InputProps={{
                              readOnly: true
                            }}
                            sx={{ 
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.875rem',
                                bgcolor: 'background.paper',
                                color: 'text.secondary'
                              }
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleGenerateObsLink}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Generate
                          </Button>
                        </Box>
                      )}
                      <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                        ℹ️ Use this link in OBS Studio as a Browser Source for real-time auction overlay
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {/* Main Screen Overlay Link */}
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'secondary.50' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Main Screen Overlay Link</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={`https://myliveauction.com/live-auction/${auctionId}/main-screen-overlay`}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const mainScreenLink = `https://myliveauction.com/live-auction/${auctionId}/main-screen-overlay`;
                                    navigator.clipboard.writeText(mainScreenLink);
                                    alert('Main screen overlay link copied to clipboard!');
                                  }}
                                  sx={{ mr: -1 }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem',
                              bgcolor: 'background.paper'
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const mainScreenLink = `https://myliveauction.com/live-auction/${auctionId}/main-screen-overlay`;
                            window.open(mainScreenLink, '_blank');
                          }}
                          sx={{ minWidth: 'auto', px: 2 }}
                        >
                          Preview
                        </Button>
                      </Box>
                      <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
                        ℹ️ Use this link for the main display screen during the auction
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </React.Fragment>
            )}

            {/* Teams Tab */}
            {activeTab === 1 && (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                  <Typography variant="h6" sx={{ flex: 1 }}>Teams Management</Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                      size="small"
                      placeholder="Search teams..."
                      value={searchTeam}
                      onChange={(e) => setSearchTeam(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openAddTeamModal}
                      sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      Add Team
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
                  <Table sx={{ minWidth: { xs: 650, sm: 750 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Logo</TableCell>
                        <TableCell>Team Name</TableCell>
                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Owner</TableCell>
                        <TableCell>Initials</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Budget</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Email</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTeams.map((team) => (
                        <TableRow key={team._id}>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {team.teamLogo ? (
                              <Box
                                component="img"
                                src={team.teamLogo}
                                alt={`${team.teamName} logo`}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  objectFit: 'cover',
                                  border: '1px solid #e0e0e0'
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  backgroundColor: '#f5f5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #e0e0e0'
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  {team.teamInitials || 'No Logo'}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">{team.teamName}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
                                {team.ownerName} • ${team.teamBudget}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{team.ownerName}</TableCell>
                          <TableCell>{team.teamInitials}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>${team.teamBudget}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{team.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 0.5 }}>
                              <Button
                                size="small"
                                startIcon={<StarIcon />}
                                onClick={() => openIconPlayerModal(team)}
                                sx={{ minWidth: { xs: 'auto', sm: 'inherit' } }}
                              >
                                <span className="hidden sm:inline">Add Icon</span>
                                <span className="sm:hidden">⭐</span>
                              </Button>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  setSelectedTeam(team);
                                  setShowUpdateTeamModal(true);
                                }}
                                sx={{ minWidth: { xs: 'auto', sm: 'inherit' } }}
                              >
                                <span className="hidden sm:inline">Edit</span>
                                <span className="sm:hidden">✏️</span>
                              </Button>
                              <Button
                                size="small"
                                startIcon={<DeleteIcon />}
                                color="error"
                                onClick={() => handleDeleteTeam(team._id)}
                                sx={{ minWidth: { xs: 'auto', sm: 'inherit' } }}
                              >
                                <span className="hidden sm:inline">Delete</span>
                                <span className="sm:hidden">🗑️</span>
                              </Button>
                            </Box>
                            {/* Display icon players for this team */}
                            {team.iconPlayers && team.iconPlayers.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  Icon Players:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {team.iconPlayers.slice(0, 3).map((player, index) => (
                                    <Chip
                                      key={player._id}
                                      label={player.playerName}
                                      size="small"
                                      sx={{ 
                                        height: '20px',
                                        '& .MuiChip-label': {
                                          fontSize: '0.7rem',
                                          px: 0.5
                                        }
                                      }}
                                    />
                                  ))}
                                  {team.iconPlayers.length > 3 && (
                                    <Chip
                                      label={`+${team.iconPlayers.length - 3} more`}
                                      size="small"
                                      sx={{ 
                                        height: '20px',
                                        '& .MuiChip-label': {
                                          fontSize: '0.7rem',
                                          px: 0.5
                                        }
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredTeams.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6">No teams found</Typography>
                    <Typography variant="body2">
                      {searchTeam ? 'Try adjusting your search terms' : 'Add your first team to get started'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Players Tab */}
            {activeTab === 2 && (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
                  <Typography variant="h6" sx={{ flex: 1 }}>Players Management</Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                      size="small"
                      placeholder="Search players..."
                      value={searchPlayer}
                      onChange={(e) => setSearchPlayer(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setShowAddPlayerModal(true)}
                      sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      Add Player
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
                  <Table sx={{ minWidth: { xs: 650, sm: 800 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Photo</TableCell>
                        <TableCell>Player Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Base Value</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Matches</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Runs</TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Wickets</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {playersToDisplay.map((player) => (
                        <TableRow key={player._id}>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {player.playerLogo ? (
                              <Box
                                component="img"
                                src={player.playerLogo}
                                alt={`${player.playerName} photo`}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '1px solid #e0e0e0'
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: '50%',
                                  backgroundColor: '#f5f5f5',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #e0e0e0'
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No Photo
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">{player.playerName}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' } }}>
                                {player.playerType} • ${player.baseValue}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{player.playerType}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{`$${player.baseValue}`}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{player.stats?.matches || '0'}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{player.stats?.runs || '0'}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{player.stats?.wickets || '0'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 0.5 }}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                  setSelectedPlayer(player);
                                  setShowUpdatePlayerModal(true);
                                }}
                                sx={{ minWidth: { xs: 'auto', sm: 'inherit' } }}
                              >
                                <span className="hidden sm:inline">Edit</span>
                                <span className="sm:hidden">✏️</span>
                              </Button>
                              <Button
                                size="small"
                                startIcon={<DeleteIcon />}
                                color="error"
                                onClick={() => handleDeletePlayer(player._id)}
                                sx={{ minWidth: { xs: 'auto', sm: 'inherit' } }}
                              >
                                <span className="hidden sm:inline">Delete</span>
                                <span className="sm:hidden">🗑️</span>
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[8, 16, 24]}
                  component="div"
                  count={filteredPlayers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ 
                    display: filteredPlayers.length > 8 ? 'flex' : 'none',
                    justifyContent: 'center'
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Update Auction Modal */}
      <Dialog open={showUpdateAuctionModal} onClose={() => setShowUpdateAuctionModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Auction</DialogTitle>
        <DialogContent>
          <TextField
            label="Auction Title"
            name="title"
            value={auctionData.title || ''}
            onChange={handleAuctionInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Sport"
            name="sport"
            select
            value={auctionData.sport || 'cricket'}
            onChange={handleAuctionInputChange}
            fullWidth
            margin="normal"
          >
            <option value="cricket">Cricket</option>
            <option value="volleyball">Volleyball</option>
          </TextField>
          <TextField
            label="Organizer"
            name="organizer"
            value={auctionData.organizer || ''}
            onChange={handleAuctionInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Date & Time"
            name="date"
            type="datetime-local"
            value={auctionData.date || ''}
            onChange={handleAuctionInputChange}
            fullWidth
            margin="normal"
            required
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Location"
            name="location"
            value={auctionData.location || ''}
            onChange={handleAuctionInputChange}
            fullWidth
            margin="normal"
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Default Team Budgets ($)"
                name="defaultTeamBudgets"
                type="number"
                value={auctionData.defaultTeamBudgets || ''}
                onChange={handleAuctionInputChange}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Players per Team"
                name="playersPerTeam"
                type="number"
                value={auctionData.playersPerTeam || ''}
                onChange={handleAuctionInputChange}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
          </Grid>
          <TextField
            label="Base Value ($)"
            name="baseValue"
            type="number"
            value={auctionData.baseValue || ''}
            onChange={handleAuctionInputChange}
            fullWidth
            margin="normal"
            required
          />
          <input
            type="file"
            name="auctionLogo"
            onChange={handleAuctionLogoChange}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Auction Logo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpdateAuctionModal(false)}>Cancel</Button>
          <Button onClick={handleUpdateAuction} variant="contained" color="primary">Update Auction</Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Team Modal */}
      <Dialog open={showAddTeamModal} onClose={() => setShowAddTeamModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Team</DialogTitle>
        <DialogContent>
          <TextField
            label="Team Name"
            name="teamName"
            value={newTeam.teamName}
            onChange={(e) => setNewTeam({...newTeam, teamName: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Owner Name"
            name="ownerName"
            value={newTeam.ownerName}
            onChange={(e) => setNewTeam({...newTeam, ownerName: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Team Initials"
            name="teamInitials"
            value={newTeam.teamInitials}
            onChange={(e) => setNewTeam({...newTeam, teamInitials: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Team Budget ($)"
            name="teamBudget"
            type="number"
            value={newTeam.teamBudget}
            onChange={(e) => setNewTeam({...newTeam, teamBudget: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={newTeam.email}
            onChange={(e) => setNewTeam({...newTeam, email: e.target.value})}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={newTeam.phoneNumber}
            onChange={(e) => setNewTeam({...newTeam, phoneNumber: e.target.value})}
            fullWidth
            margin="normal"
          />
          <input
            type="file"
            name="teamLogo"
            onChange={(e) => handleNewTeamFileChange(e)}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Team Logo
          </Typography>
          <input
            type="file"
            name="teamOwnerLogo"
            onChange={(e) => handleNewTeamFileChange(e)}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Team Owner Logo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddTeamModal(false)}>Cancel</Button>
          <Button onClick={handleAddTeam} variant="contained" color="primary">Add Team</Button>
        </DialogActions>
      </Dialog>
      
      {/* Update Team Modal */}
      <Dialog open={showUpdateTeamModal} onClose={() => setShowUpdateTeamModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Team</DialogTitle>
        <DialogContent>
          <TextField
            label="Team Name"
            name="teamName"
            value={selectedTeam.teamName || ''}
            onChange={(e) => setSelectedTeam({...selectedTeam, teamName: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Owner Name"
            name="ownerName"
            value={selectedTeam.ownerName || ''}
            onChange={(e) => setSelectedTeam({...selectedTeam, ownerName: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Team Initials"
            name="teamInitials"
            value={selectedTeam.teamInitials || ''}
            onChange={(e) => setSelectedTeam({...selectedTeam, teamInitials: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Team Budget ($)"
            name="teamBudget"
            type="number"
            value={selectedTeam.teamBudget || ''}
            onChange={(e) => setSelectedTeam({...selectedTeam, teamBudget: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={selectedTeam.email || ''}
            onChange={(e) => setSelectedTeam({...selectedTeam, email: e.target.value})}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={selectedTeam.phoneNumber || ''}
            onChange={(e) => setSelectedTeam({...selectedTeam, phoneNumber: e.target.value})}
            fullWidth
            margin="normal"
          />
          <input
            type="file"
            name="teamLogo"
            onChange={handleSelectedTeamFileChange}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Team Logo
          </Typography>
          <input
            type="file"
            name="teamOwnerLogo"
            onChange={handleSelectedTeamFileChange}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Team Owner Logo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpdateTeamModal(false)}>Cancel</Button>
          <Button onClick={handleUpdateTeam} variant="contained" color="primary">Update Team</Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Player Modal */}
      <Dialog open={showAddPlayerModal} onClose={() => setShowAddPlayerModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Player</DialogTitle>
        <DialogContent>
          <TextField
            label="Player Name"
            name="playerName"
            value={playerData.playerName}
            onChange={(e) => setPlayerData({...playerData, playerName: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Player Type</InputLabel>
            <Select
              name="playerType"
              value={playerData.playerType}
              onChange={(e) => setPlayerData({...playerData, playerType: e.target.value})}
              label="Player Type"
            >
              <MenuItem value="">Select Player Type</MenuItem>
              <MenuItem value="Batsman">Batsman</MenuItem>
              <MenuItem value="Bowler">Bowler</MenuItem>
              <MenuItem value="All-Rounder">All-Rounder</MenuItem>
              <MenuItem value="Wicket-Keeper">Wicket-Keeper</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Base Value ($)"
            name="baseValue"
            type="number"
            value={playerData.baseValue}
            onChange={(e) => setPlayerData({...playerData, baseValue: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Matches"
                name="matches"
                type="number"
                value={playerData.matches}
                onChange={(e) => setPlayerData({...playerData, matches: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Runs"
                name="runs"
                type="number"
                value={playerData.runs}
                onChange={(e) => setPlayerData({...playerData, runs: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Wickets"
                name="wickets"
                type="number"
                value={playerData.wickets}
                onChange={(e) => setPlayerData({...playerData, wickets: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Strike Rate"
                name="strikeRate"
                type="number"
                value={playerData.strikeRate}
                onChange={(e) => setPlayerData({...playerData, strikeRate: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Half Centuries"
                name="halfCenturies"
                type="number"
                value={playerData.halfCenturies}
                onChange={(e) => setPlayerData({...playerData, halfCenturies: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Centuries"
                name="centuries"
                type="number"
                value={playerData.centuries}
                onChange={(e) => setPlayerData({...playerData, centuries: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <input
            type="file"
            name="playerLogo"
            onChange={handlePlayerFileChange}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Player Photo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddPlayerModal(false)}>Cancel</Button>
          <Button onClick={handleAddPlayer} variant="contained" color="primary">Add Player</Button>
        </DialogActions>
      </Dialog>
      
      {/* Update Player Modal */}
      <Dialog open={showUpdatePlayerModal} onClose={() => setShowUpdatePlayerModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Player</DialogTitle>
        <DialogContent>
          <TextField
            label="Player Name"
            name="playerName"
            value={selectedPlayer.playerName || ''}
            onChange={(e) => setSelectedPlayer({...selectedPlayer, playerName: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Player Type</InputLabel>
            <Select
              name="playerType"
              value={selectedPlayer.playerType || ''}
              onChange={(e) => setSelectedPlayer({...selectedPlayer, playerType: e.target.value})}
              label="Player Type"
            >
              <MenuItem value="">Select Player Type</MenuItem>
              <MenuItem value="Batsman">Batsman</MenuItem>
              <MenuItem value="Bowler">Bowler</MenuItem>
              <MenuItem value="All-Rounder">All-Rounder</MenuItem>
              <MenuItem value="Wicket-Keeper">Wicket-Keeper</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Base Value ($)"
            name="baseValue"
            type="number"
            value={selectedPlayer.baseValue || ''}
            onChange={(e) => setSelectedPlayer({...selectedPlayer, baseValue: e.target.value})}
            fullWidth
            margin="normal"
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Matches"
                name="matches"
                type="number"
                value={selectedPlayer.matches || ''}
                onChange={(e) => setSelectedPlayer({...selectedPlayer, matches: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Runs"
                name="runs"
                type="number"
                value={selectedPlayer.runs || ''}
                onChange={(e) => setSelectedPlayer({...selectedPlayer, runs: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Wickets"
                name="wickets"
                type="number"
                value={selectedPlayer.wickets || ''}
                onChange={(e) => setSelectedPlayer({...selectedPlayer, wickets: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Strike Rate"
                name="strikeRate"
                type="number"
                value={selectedPlayer.strikeRate || ''}
                onChange={(e) => setSelectedPlayer({...selectedPlayer, strikeRate: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Half Centuries"
                name="halfCenturies"
                type="number"
                value={selectedPlayer.halfCenturies || ''}
                onChange={(e) => setSelectedPlayer({...selectedPlayer, halfCenturies: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Centuries"
                name="centuries"
                type="number"
                value={selectedPlayer.centuries || ''}
                onChange={(e) => setSelectedPlayer({...selectedPlayer, centuries: e.target.value})}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <input
            type="file"
            name="playerLogo"
            onChange={handlePlayerFileChange}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Player Photo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpdatePlayerModal(false)}>Cancel</Button>
          <Button onClick={handleUpdatePlayer} variant="contained" color="primary">Update Player</Button>
        </DialogActions>
      </Dialog>
      
      {/* Icon Player Selection Modal */}
      <Dialog open={showIconPlayerModal} onClose={closeIconPlayerModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Icon Players for {selectedTeamForIcon?.teamName || 'Team'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select existing players to mark as icons for this team:
          </Typography>
          
          {players.length > 0 ? (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {players.map((player) => (
                <ListItem 
                  key={player._id} 
                  onClick={() => {
                    // Prevent selection of players already assigned to another team
                    if (!player.team || player.team._id === selectedTeamForIcon?._id) {
                      handleIconPlayerSelect(player._id);
                    }
                  }} 
                  button
                  disabled={player.team && player.team._id !== selectedTeamForIcon?._id}
                >
                  <ListItemIcon>
                    <Checkbox 
                      edge="start" 
                      checked={selectedIconPlayers.includes(player._id)}
                      tabIndex={-1} 
                      disableRipple 
                      disabled={player.team && player.team._id !== selectedTeamForIcon?._id}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={player.playerName} 
                    secondary={
                      <>
                        {player.playerType}
                        {player.team && player.team._id !== selectedTeamForIcon?._id && (
                          <Chip 
                            label={`Sold to ${player.team.teamName}`} 
                            size="small" 
                            sx={{ ml: 1, bgcolor: 'warning.light', color: 'warning.contrastText' }} 
                          />
                        )}
                        {player.team && player.team._id === selectedTeamForIcon?._id && (
                          <Chip 
                            label="Already in team" 
                            size="small" 
                            sx={{ ml: 1, bgcolor: 'info.light', color: 'info.contrastText' }} 
                          />
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No players available in this auction.
            </Typography>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={openCreateIconPlayerModal}
            >
              Create New Icon Player
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIconPlayerModal}>Cancel</Button>
          <Button onClick={handleSaveIconPlayers} variant="contained" color="primary">
            Save Icon Players
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Create Icon Player Modal */}
      <Dialog open={showCreateIconPlayerModal} onClose={closeCreateIconPlayerModal} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Icon Player</DialogTitle>
        <DialogContent>
          <TextField
            label="Player Name"
            name="playerName"
            value={newIconPlayer.playerName}
            onChange={handleNewIconPlayerChange}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Player Type</InputLabel>
            <Select
              name="playerType"
              value={newIconPlayer.playerType}
              onChange={handleNewIconPlayerChange}
              label="Player Type"
            >
              <MenuItem value="">Select Player Type</MenuItem>
              <MenuItem value="Batsman">Batsman</MenuItem>
              <MenuItem value="Bowler">Bowler</MenuItem>
              <MenuItem value="All-Rounder">All-Rounder</MenuItem>
              <MenuItem value="Wicket-Keeper">Wicket-Keeper</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Base Value ($)"
            name="baseValue"
            type="number"
            value={newIconPlayer.baseValue}
            onChange={handleNewIconPlayerChange}
            fullWidth
            margin="normal"
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Matches"
                name="matches"
                type="number"
                value={newIconPlayer.matches}
                onChange={handleNewIconPlayerChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Runs"
                name="runs"
                type="number"
                value={newIconPlayer.runs}
                onChange={handleNewIconPlayerChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Wickets"
                name="wickets"
                type="number"
                value={newIconPlayer.wickets}
                onChange={handleNewIconPlayerChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Strike Rate"
                name="strikeRate"
                type="number"
                value={newIconPlayer.strikeRate}
                onChange={handleNewIconPlayerChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Half Centuries"
                name="halfCenturies"
                type="number"
                value={newIconPlayer.halfCenturies}
                onChange={handleNewIconPlayerChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Centuries"
                name="centuries"
                type="number"
                value={newIconPlayer.centuries}
                onChange={handleNewIconPlayerChange}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateIconPlayerModal}>Cancel</Button>
          <Button onClick={handleCreateIconPlayer} variant="contained" color="primary">Create Player</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminDashboardAuctionDetails;